import Cookies from 'js-cookie';
import { SetStateAction } from 'react';
import {
  DEFAULT_MOBILEPOSTPAID_BUNDLE,
  DEFAULT_WIRELINE_BUNDLE,
  ONE_ECOMMERCE_INTERNET_SUMMARY,
  ONE_ECOMMERCE_SUMMARY,
  RATEPLAN,
  TV_SERVICE_OPTION,
  VCTYPE
} from '../../utils/constants';
import { createCartV2, fetchShogunOfferData } from '../../services/LineTableService';
import {
  checkLinkWithLang,
  getElementMaximumHeight,
  getLocalStorageWithExpiry,
  returnPriceByType
} from '../../utils/utils';
import { trackNewDatalayerEvent } from '../../utils/AnalyticsUtils';
import {
  ADDTOCART,
  COMBINEDEVICE,
  GAEVENT,
  LINETABLE,
  LINETABLEDEVICE,
  LINETABLEPRODUCT
} from '../../Analytics/AnalyticsConstants';
import { getEcomCartInfo, trackEligibleItem } from '../../models/Analytics/items';
import { AtlOffer, OfferValidation, PegaProductRequestBase } from '../lineTable/lineTable.types';
import { calculateDateTime } from './StaticLineTabelTimer/util';

export const isTimerActive = (startTime: string, endTime: string): boolean => {
  try {
    const now = new Date();
    const start = calculateDateTime(new Date(startTime.replace(/-/g, '/')));
    const end = calculateDateTime(new Date(endTime.replace(/-/g, '/')));

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return false;
    }

    return now >= start && now < end;
  } catch {
    return false;
  }
};

export const getTimeUntilNextTimerEvent = (startTime: string, endTime: string): number => {
  try {
    const start = calculateDateTime(new Date(startTime.replace(/-/g, '/')));
    const end = calculateDateTime(new Date(endTime.replace(/-/g, '/')));

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return 0;
    }

    const now = new Date().getTime();
    const startTimeMs = start.getTime();
    const endTimeMs = end.getTime();

    // Next transition is start when timer is upcoming, otherwise end when active.
    if (now < startTimeMs) {
      return Math.max(0, startTimeMs - now);
    }
    if (now >= startTimeMs && now < endTimeMs) {
      return Math.max(0, endTimeMs - now);
    }

    return 0;
  } catch {
    return 0;
  }
};

export const fetchStaticProductIds = (
  productList: any,
  productId: any,
  bundle4PProduct?: boolean,
  tvOptionIds?: any,
  //US_31295
  pricesToggleState?: any
): any => {
  let enableStream = false;
  let promotionId = '';
  productList?.map((data: any) => {
    const productRequest: any = {
      id: '',
      promoIds: []
    };

    const productData = data?.products?.[0]?.reference?.content?.product?.[0];
    const promotionData = data?.promotion?.[0]?.reference?.content?.promoDescription?.[0];
    promotionId = promotionData?.promotionId;
    productRequest['id'] = productData?.productId;
    const timer = data?.timer?.[0];
    const timerIsActive = timer ? isTimerActive(timer.startTime, timer.endTime) : false;
    const timerPromotionId =
      timer?.promotion?.[0]?.reference?.content?.promoDescription?.[0]?.promotionId;
    const defaultPromotionId =
      pricesToggleState && pricesToggleState === true && promotionData?.promotionId === ''
        ? DEFAULT_MOBILEPOSTPAID_BUNDLE
        : promotionData?.promotionId;

    //US_31295 - add default promo only when timer is not active and timer has no promo
    if (timerIsActive && timerPromotionId) {
      productRequest?.promoIds?.push(timerPromotionId);
    } else {
      productRequest?.promoIds?.push(defaultPromotionId);
    }

    //for 4P product
    if (bundle4PProduct) {
      const mobilePromotionData =
        data?.mobilePromotion?.[0]?.reference?.content?.promoDescription?.[0];
      productRequest?.promoIds?.push(mobilePromotionData?.promotionId);
    }

    productId?.push(productRequest);
  });
  if (enableStream) {
    tvOptionIds?.map((option: any) => {
      const tvOptionObj: any = {
        id: '',
        promoIds: []
      };
      tvOptionObj['id'] = option?.text;
      tvOptionObj?.promoIds?.push(promotionId);

      productId?.push(tvOptionObj);
    });
  }
};

export const fetchPegaProducts = (
  content: any,
  pegaCustomerInterest?: string,
  placements?: string
): PegaProductRequestBase => {
  // const url = window?.location?.pathname?.split('/') ?? [];
  // const pathname = url[url.length - 1];
  //set customerInterest as content?.tabCaption capitalize inital letter and after space and then remove spaces - from Other plans into OtherPlans
  const customerInterest = content?.tabCaption
    ?.replace(/\b\w/g, (l: string) => l.toUpperCase())
    .replace(/\s+/g, '');

  const offerValidations = [
    {
      key: 'Segment',
      type: 'CustomerType',
      value: 'Residential'
    },
    {
      key: 'OrderType',
      type: 'CustomerInterest',
      value: 'New'
    },
    {
      key: 'CurrentPage',
      type: 'Context',
      value: 'mobile-subscription' //pathname ?? ''
    },
    {
      key: 'Category',
      type: 'CustomerInterest',
      value: customerInterest ?? ''
    },
    {
      key: 'RequestedOfferCount',
      type: 'Context',
      value: content?.pegaRequestOfferCount?.toString() ?? '3'
    },
    pegaCustomerInterest
      ? {
          key: 'Tag',
          type: 'CustomerInterest',
          value: pegaCustomerInterest
        }
      : null
  ].filter(Boolean) as OfferValidation[];

  const atlOffers: AtlOffer = {
    placements,
    offerValidations
  };

  const pegaProducts: PegaProductRequestBase = {
    atlOffers: [atlOffers]
  };

  return pegaProducts;
};

export const checkGiftOrCashback = (data: any): any => {
  let showGift = false;
  let cashbackAvailable = false;
  data?.map((product: any) => {
    product?.promotionInfo?.map((offer: any) => {
      if (offer?.hardwareGift) showGift = true;
      if (offer?.cashBack) cashbackAvailable = true;
    });
  });
  return { showGift, cashbackAvailable };
};

export const staticCreateCart = (
  setShowLoader: { (value: SetStateAction<boolean>): void; (arg0: boolean): void },
  cartEndPoint: string,
  giftItem: any,
  uniqueProductData: any,
  mainContent: any,
  showPrice: any,
  isMobileFlow: boolean,
  radioValue: any,
  eligibleBundle: any,
  address: any,
  setShowLinecheck: any,
  showLinecheck: any,
  lineCheckCallBack: any,
  enableP2P: any,
  P2PData: any,
  content: any,
  tvOptions?: any,
  discountRef?: string,
  discountVersion?: string,
  // Shogun/rewards (loyalty) flow — forwarded verbatim to createCartV2 so it
  // can append the pointsBurnt note at the item level (RATEPLAN for MOBILE,
  // internet item for FIXNET).
  rewardsInfo?: { pointsBurnt?: string | number; rewardOfferId?: string | number; gloxTokenId?: string | null }
): any => {
  if (isMobileFlow) {
    setShowLoader(true);
    const vcType: { voucher: string; voucherType: string } = getLocalStorageWithExpiry(VCTYPE);
    Object.keys(uniqueProductData)?.length !== 0 &&
      createCartV2(
        uniqueProductData,
        '',
        showPrice?.promoID ? showPrice?.promoID : DEFAULT_MOBILEPOSTPAID_BUNDLE,
        cartEndPoint,
        'mobile',
        giftItem,
        enableP2P,
        P2PData,
        mainContent?.productContainer?.[0]?.isQoQaFlow,
        vcType?.voucher,
        null,
        null,
        discountRef,
        discountVersion,
        rewardsInfo
      )
        .then((res: any) => {
          Cookies.remove('one-commerce-cartId', { path: '/' });
          // saving newly created cartid in the cookies.
          Cookies.set('one-commerce-cartId', res?.id, { path: '/' });
          Cookies.set('cartProduct', showPrice?.id, { path: '/' });
          trackEligibleItem(ADDTOCART, uniqueProductData, '', LINETABLEPRODUCT);
          window.location.href = mainContent?.summaryPageUrl?.cached_url;
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.log(err);
          setShowLoader(false);
        })
        .finally(() => {
          setShowLoader(false);
        });
  } else {
    const linecheckId: any =
      typeof window !== 'undefined' && Cookies.get('one-commerce-linecheckcorrelationid');
    if (!eligibleBundle || !address || !linecheckId) {
      mainContent?.enableLinecheckOverlay ? setShowLinecheck(!showLinecheck) : lineCheckCallBack();
    } else {
      setShowLoader(true);
      uniqueProductData.length !== 0 &&
        createCartV2(
          uniqueProductData,
          radioValue,
          showPrice?.promoID ? showPrice?.promoID : DEFAULT_WIRELINE_BUNDLE,
          cartEndPoint,
          'fixnet',
          giftItem,
          '',
          '',
          false,
          '',
          '',
          tvOptions,
          undefined,
          undefined,
          rewardsInfo
        )
          .then((res: any) => {
            typeof window !== 'undefined' && Cookies.remove('one-commerce-cartId', { path: '/' });
            // saving newly created cartid in the cookies.
            typeof window !== 'undefined' &&
              Cookies.set('one-commerce-cartId', res?.id, { path: '/' });
            typeof window !== 'undefined' &&
              Cookies.set('cartProduct', showPrice?.id, { path: '/' });
            typeof window !== 'undefined' && Cookies.remove('landline');
            trackEligibleItem(ADDTOCART, uniqueProductData, '', LINETABLE);
            const productUrl = checkLinkWithLang(content?.productBasedCheckoutUrl?.cached_url);
            let recommendAccessory = false;
            /**Check if bundle or Internet and add it to cart request*/
            if (uniqueProductData[0]?.isBundle) {
              uniqueProductData[0]?.bundledOffers?.map((item: any) => {
                if (
                  item?.productType?.toLowerCase() === 'internet' ||
                  item?.productType?.toLowerCase() == 'tv'
                ) {
                  if (item?.recommendAccessory) {
                    recommendAccessory = item?.recommendAccessory;
                  }
                }
                // setting to false incase of Mobile product Type as it is currently out of scope
                if (item?.productType?.toLowerCase() === 'mobile') {
                  recommendAccessory = false;
                  return;
                }
              });
            } else {
              /**Add standlone product such as internet to cart request*/
              if (uniqueProductData?.[0]?.recommendAccessory) {
                recommendAccessory = uniqueProductData?.[0]?.recommendAccessory;
              }
            }
            //setting the session to identify internet flow for mia for win
            if (recommendAccessory) {
              const sessionObj = { flow: 'WIN' };
              sessionStorage.setItem(ONE_ECOMMERCE_SUMMARY, JSON.stringify(sessionObj));
            }

            const finalURL = productUrl
              ? productUrl
              : content?.enableTVStreamingOptionsStepper && mainContent?.vasStepperURL?.cached_url
                ? mainContent?.vasStepperURL?.cached_url
                : recommendAccessory && mainContent?.multiItemPageRedirectionUrl?.cached_url
                  ? mainContent?.multiItemPageRedirectionUrl?.cached_url
                  : mainContent?.summaryPageUrl?.cached_url;

            window.location.href = finalURL;
          })
          .catch((err: any) => {
            // eslint-disable-next-line no-console
            console.log(err);
          })
          .finally(() => {
            setShowLoader(false);
          });
    }
  }
};

export const findHeight = (tabIndex: number, cardsLength: number) => {
  const setTimerSectionHeight = () => {
    let maxActiveTimerHeight = 0;
    const timerSections: HTMLElement[] = [];

    [...Array(cardsLength)].forEach((_, index) => {
      const timerSection = document.getElementById(`timer-section${tabIndex}${index}`);
      if (timerSection) {
        timerSections.push(timerSection);
        const isTimerActive = timerSection.dataset.timerActive === 'true';
        if (isTimerActive) {
          const spacer = timerSection.firstElementChild as HTMLElement | null;
          // Read the inline style target height instead of clientHeight so that
          // an in-progress CSS transition does not yield an intermediate value.
          const targetHeight = parseFloat(spacer?.style.height || '0') || 0;
          maxActiveTimerHeight = Math.max(maxActiveTimerHeight, targetHeight);
        }
      }
    });

    timerSections.forEach((timerSection) => {
      const isTimerActive = timerSection.dataset.timerActive === 'true';
      timerSection.style.transition = 'height 0.3s ease';
      timerSection.style.height = isTimerActive ? '0px' : `${maxActiveTimerHeight}px`;
    });
  };

  getElementMaximumHeight('headingV3' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('descriptionV3' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('titleV3' + `${tabIndex}`, cardsLength);
  setTimerSectionHeight();
  getElementMaximumHeight('detailsectionv3' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('product-configv3' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('gift-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('benefits-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('order-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('price-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('benefits-price-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('contract-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('exclusive-benefits-section' + `${tabIndex}`, cardsLength);
  getElementMaximumHeight('discountedpricetoggle-section' + `${tabIndex}`, cardsLength);
};

const setMobileSummarySession = (
  promoId: string,
  uniqueProductData: any,
  pretopostvalues?: any,
  showPrice?: any,
  product?: any,
  pegaIntegration?: any
) => {
  const productPrice = uniqueProductData;
  const base: number = productPrice?.[0]?.prices?.[0]?.value ?? 0;
  const discount = productPrice?.[0]?.derivedPrice?.[0]?.value ?? 0;
  const contractDuration = productPrice?.[0]?.promotionInfo?.[0]?.contractDuration ?? 0;
  const campaignDuration = productPrice?.[0]?.promotionInfo?.[0]?.campaignDuration ?? 0;
  const pretopostvalue = pretopostvalues ?? '';
  //PEGA Integration
  const discountRef = pegaIntegration && uniqueProductData?.[0]?.promotionInfo?.[0]?.id;
  const discountVersion = pegaIntegration && uniqueProductData?.[0]?.promotionInfo?.[0]?.version;
  const clientDiscountInfo = pegaIntegration
    ? {
        discountRef: discountRef,
        discountType: 'DISCOUNT'
      }
    : null;
  const note = [];
  note?.push({
    author: 'offerType',
    text: 'ATL'
  });
  note?.push({
    author: 'externalPromotionName',
    text: discountRef
  });
  note?.push({
    author: 'offerVersion',
    text: discountVersion
  });

  const extraNotes = pegaIntegration ? note : null;
  const urlParams = new URLSearchParams(window.location.search);
  const flow = urlParams.get('flow');
  const sessionObj: any = {
    mobileSubscription: productPrice?.[0]?.id ?? '',
    mobileSubscriptionPrice: discount > 0 ? discount : base,
    mobileSubscriptionPromo: promoId,
    subGroup: 'MOBILE',
    duration: Number(campaignDuration > 0 ? campaignDuration : contractDuration),
    basePrice: base,
    withoutPromo: false,
    pretopostvalue: pretopostvalue,
    upBenefit:
      showPrice.mob_benefit_category == 'MULTIMOBILE' || showPrice.mob_benefit_category == 'FMC',
    ultimateBundlePromo: product?.ultimateBundlePromotion ?? '',
    clientDiscountInfo: clientDiscountInfo,
    extraNotes: extraNotes,
    isOttFlow: flow === 'ott' ? true : false
  };

  typeof window !== undefined &&
    sessionStorage.setItem('one-ecommerce-mobileSummary', JSON.stringify(sessionObj));
};

export const combineWithSmartPhone = (
  showPrice: any,
  storyContent: any,
  uniqueProductData: any,
  responseData?: any,
  product?: any,
  pegaIntegration?: boolean
) => {
  const pretopostvalues = {
    msisdn: responseData?.msisdn ?? '',
    ratePlan: responseData?.ratePlan ?? '',
    ratePlanType: responseData?.ratePlanType ?? '',
    productId: responseData ? showPrice?.id : ''
  };
  const promoId = showPrice?.promoID ? showPrice?.promoID : DEFAULT_MOBILEPOSTPAID_BUNDLE;
  let Params = '';
  Params = `?componentCode=&productType=subscription&segmentCode=RC&prefPromo=${
    promoId ?? ''
  }&productCode=${showPrice?.id ?? ''}`;
  window.location.href = storyContent?.addSmartPhone?.[0]?.buttonLink?.cached_url + Params;
  setMobileSummarySession(
    promoId,
    uniqueProductData,
    responseData && pretopostvalues,
    showPrice,
    product,
    pegaIntegration
  );
  trackNewDatalayerEvent(
    GAEVENT,
    COMBINEDEVICE,
    getEcomCartInfo(uniqueProductData, '', LINETABLEDEVICE)
  );
  trackEligibleItem(ADDTOCART, uniqueProductData, '', LINETABLEDEVICE);
};

const setFixnetSummarySession = (
  promoId: string,
  uniqueProductData: any,
  showPrice?: any,
  product?: any,
  pegaIntegration?: any,
  productName?: string,
  recommendAccessory?: boolean | undefined
) => {
  const productPrice = uniqueProductData;
  const base: number = productPrice?.[0]?.prices?.[0]?.value ?? 0;
  const discount = productPrice?.[0]?.derivedPrice?.[0]?.value ?? 0;
  const contractDuration =
    productPrice?.[0]?.promotionInfo?.[0]?.contractDuration ?? showPrice?.contractDuration ?? 0;
  const campaignDuration = productPrice?.[0]?.promotionInfo?.[0]?.campaignDuration ?? 0;

  //PEGA Integration
  const discountRef = pegaIntegration && uniqueProductData?.[0]?.promotionInfo?.[0]?.id;
  const discountVersion = pegaIntegration && uniqueProductData?.[0]?.promotionInfo?.[0]?.version;
  const clientDiscountInfo = pegaIntegration
    ? {
        discountRef: discountRef,
        discountType: 'DISCOUNT'
      }
    : null;
  const note = [];
  note?.push({
    author: 'offerType',
    text: 'ATL'
  });
  note?.push({
    author: 'externalPromotionName',
    text: discountRef
  });
  note?.push({
    author: 'offerVersion',
    text: discountVersion
  });

  const extraNotes = pegaIntegration ? note : null;

  const sessionObj: any = {
    internetSubscription: productName ?? '',
    internetSubscriptionId: productPrice?.[0]?.id ?? '',
    internetSubscriptionPrice: discount > 0 ? discount : base,
    internetSubscriptionPromo: promoId,
    subGroup: 'INTERNET',
    duration: Number(campaignDuration > 0 ? campaignDuration : contractDuration),
    basePrice: base,
    withPromo: discount > 0 ? true : false,
    upBenefit:
      showPrice.mob_benefit_category == 'MULTIMOBILE' || showPrice.mob_benefit_category == 'FMC',
    ultimateBundlePromo: product?.ultimateBundlePromotion ?? '',
    clientDiscountInfo: clientDiscountInfo,
    extraNotes: extraNotes,
    productType: 'subscription',
    recommendAccessory: recommendAccessory
  };

  typeof window !== undefined &&
    sessionStorage.setItem(ONE_ECOMMERCE_INTERNET_SUMMARY, JSON.stringify(sessionObj));
};
export const combineWithVasOptions = (
  showPrice: any,
  uniqueProductData: any,
  product?: any,
  pegaIntegration?: boolean,
  productName?: string,
  recommendAccessory?: boolean
) => {
  const promoId = showPrice?.promoID ? showPrice?.promoID : DEFAULT_MOBILEPOSTPAID_BUNDLE;

  setFixnetSummarySession(
    promoId,
    uniqueProductData,
    showPrice,
    product,
    pegaIntegration,
    productName,
    recommendAccessory
  );
  trackNewDatalayerEvent(
    GAEVENT,
    COMBINEDEVICE,
    getEcomCartInfo(uniqueProductData, '', LINETABLEDEVICE)
  );
  trackEligibleItem(ADDTOCART, uniqueProductData, '', LINETABLEDEVICE);
};

export const fetchServiceOptions = (entitlement: any, isMobileFlow: boolean) => {
  let service: any;
  if (!isMobileFlow) {
    service = (entitlement || []).filter((product: any) => product?.enableTV === true);
  }
  return service;
};

export const fetchPrice = (
  product: any,
  isMobileFlow: boolean,
  pageType?: string,
  isConfigurator?: boolean,
  tvOptions?: any[],
  pegaIntegration?: boolean
): any => {
  const showPrice: any = {
    base: '',
    onetimePrice: '',
    discount: [],
    contractDuration: '',
    int_fmc: 0,
    mob_fmc: 0,
    mob_benefit_category: '',
    id: '',
    promoID: '',
    loyaltyPrice: ''
  };
  let tvOptionsBasePrice = 0;
  let tvOptionsDiscountPrice = 0;
  let tvOptionsBaseToDiscount = 0;

  Array.isArray(product) &&
    product?.map((item: any) => {
      showPrice['id'] = item?.id ?? '';
      item?.prices?.map((basePrice: any) => {
        if (basePrice?.priceType?.toLowerCase() === 'recurring') {
          if (tvOptions && tvOptions?.length > 0) {
            tvOptions?.forEach((opt: any) => {
              if (opt?.beneficiaryPrice > 0) {
                tvOptionsDiscountPrice += parseFloat(opt?.beneficiaryPrice);
              } else if (opt?.derivedPrice > 0) {
                tvOptionsDiscountPrice += parseFloat(opt?.derivedPrice);
              } else {
                tvOptionsBaseToDiscount += parseFloat(opt?.recurringPrice);
              }
              if (opt?.recurringPrice > 0) {
                tvOptionsBasePrice += parseFloat(opt?.recurringPrice);
              }
            });
            //If one of the options in the list has a discount, other options base price is stored to sum it up in the end
            if (tvOptionsDiscountPrice > 0) {
              tvOptionsDiscountPrice += tvOptionsBaseToDiscount;
            }
            showPrice['base'] = JSON.stringify(
              parseFloat(basePrice?.value) +
                (tvOptionsDiscountPrice > 0 ? tvOptionsDiscountPrice : tvOptionsBasePrice)
            );
          } else {
            showPrice['base'] = basePrice?.value;
          }
        } else if (basePrice?.priceType?.toLowerCase() === 'one time')
          showPrice['onetimePrice'] = basePrice?.value;
      });
      item?.derivedPrice?.map((discountPrice: any) => {
        const cycle = { cycleStart: '', cycleEnd: '', value: '' };
        cycle['cycleStart'] =
          pegaIntegration && !product?.[0]?.isBundle && product?.[0]?.contractDuration
            ? 1
            : discountPrice?.cycle?.cycleStart;
        cycle['cycleEnd'] =
          pegaIntegration && !product?.[0]?.isBundle && product?.[0]?.contractDuration
            ? pegaIntegration && !product?.[0]?.isBundle && product?.[0]?.contractDuration
            : discountPrice?.cycle?.cycleEnd;
        cycle['value'] = discountPrice?.value;
        if (tvOptions && tvOptions?.length > 0) {
          if (tvOptionsDiscountPrice > 0) {
            cycle['value'] = JSON.stringify(
              parseFloat(discountPrice?.value) + tvOptionsDiscountPrice
            );
          } else {
            cycle['value'] = JSON.stringify(parseFloat(discountPrice?.value) + tvOptionsBasePrice);
          }
        }
        showPrice['discount'].push(cycle);
      });
      // Calculating loyalty price introduced with DC portfolio
      if (item?.promotionInfo?.[0]?.relatedPromo?.length > 0) {
        item?.promotionInfo?.[0]?.relatedPromo?.map((promo: any) => {
          if (promo?.type === 'LOYALTY_DISCOUNT') {
            showPrice['loyaltyPrice'] = promo?.discount;
          }
        });
      }
    });

  product?.[0]?.bundledOffers?.forEach((item: any) => {
    item?.prices?.forEach((productDetails: any) => {
      if (productDetails?.priceAlteration?.length > 0) {
        productDetails?.priceAlteration?.forEach((price: any) => {
          if (price?.priceCategory == 'INTERNET_FMC') {
            showPrice['int_fmc'] += Number(price?.value);
          } else if (
            price?.priceCategory == 'MULTIMOBILE' ||
            price?.priceCategory == 'MULTIMOBILE_REWARD' ||
            price?.priceCategory == 'FMC'
          ) {
            showPrice['mob_fmc'] += Number(price?.value);
            showPrice['mob_benefit_category'] = price?.priceCategory;
          }
        });
      }
    });
    if (item?.contractDuration) {
      showPrice['contractDuration'] = item?.contractDuration; // for bundled products contract duration is taken from bundled offers
    }
    if (item?.promotionInfo?.[0]?.id && !pegaIntegration) {
      showPrice['promoID'] = item?.promotionInfo?.[0]?.id;
    }
  });
  if (!product?.[0]?.isBundle) {
    showPrice['contractDuration'] = product?.[0]?.contractDuration; // for other products
    if (!pegaIntegration) {
      showPrice['promoID'] = product?.[0]?.promotionInfo?.[0]?.id;
    }
  }
  let int_fmc = 0;
  product?.[0]?.prices?.map((productDetails: any) => {
    if (productDetails?.priceAlteration?.length > 0) {
      productDetails?.priceAlteration?.map((price: any) => {
        if (price?.priceCategory == 'INTERNET_FMC') {
          int_fmc += Number(price?.value);
        } else if (
          price?.priceCategory == 'MULTIMOBILE' ||
          price?.priceCategory == 'MULTIMOBILE_REWARD' ||
          price?.priceCategory == 'FMC'
        ) {
          showPrice['mob_fmc'] += Number(price?.value);
          showPrice['mob_benefit_category'] = price?.priceCategory;
        }
      });
    }
  });
  if (!showPrice?.contractDuration && !isMobileFlow) {
    showPrice['contractDuration'] = '12';
  }
  {
    showPrice['int_fmc'] = isConfigurator ? int_fmc : showPrice['int_fmc'];
  }
  if (pageType == '4P') {
    let bundleContractDuration = '0';
    product?.[0]?.bundledOffers?.map((offerData: any) => {
      if (offerData?.contractDuration) {
        bundleContractDuration =
          Number(bundleContractDuration) > Number(offerData?.contractDuration)
            ? bundleContractDuration
            : offerData?.contractDuration;
      }
    });
    showPrice['contractDuration'] = bundleContractDuration;
  }
  return showPrice;
};

//function for creating different array for 3 selections
export const fetchFilteredProducts = (productList: any, toggleState: any) => {
  //need to centralize for comman solution
  // Filter productList based on gigaSpeed toggle - US #363853
  let filteredList = productList;

  filteredList = productList.filter((item: any) => {
    const gigaSpeedValue = item?.gigaSpeed?.toLowerCase();
    if (toggleState?.gigaSpeed) {
      return gigaSpeedValue != 'hide';
    } else {
      return gigaSpeedValue != 'show';
    }
  });

  let mainArray = filteredList;
  const numberOfArray = 3;
  let res1 = [];
  let res2 = [];
  let res3 = [];
  for (let item of mainArray) {
    const multiIndex = item?.productIndex?.includes(',');
    const updatedMultiIndex = multiIndex && item?.productIndex?.split(',');
    if (multiIndex) {
      for (let mIndex of updatedMultiIndex) {
        for (let i = 0; i < numberOfArray; i++) {
          if (Number(mIndex) === i) {
            i === 0 ? res1.push(item) : i === 1 ? res2.push(item) : res3.push(item);
          }
        }
      }
    } else {
      for (let i = 0; i < numberOfArray; i++) {
        if (Number(item?.productIndex) === i) {
          i === 0 ? res1.push(item) : i === 1 ? res2.push(item) : res3.push(item);
        }
      }
    }
  }
  const displayContent =
    toggleState?.TV && toggleState?.Netflix ? res3 : toggleState?.TV ? res2 : res1;
  return displayContent;
};
export const fetchOttItems = (ottCartData: any) => {
  return (ottCartData?.products || [])?.map((products: any) => ({
    itemType: products?.type ?? '',
    promotionId: products?.promo ?? '',
    items: [
      {
        itemType: products?.type === TV_SERVICE_OPTION ? TV_SERVICE_OPTION : RATEPLAN,
        id: products?.id ?? ''
      }
    ]
  }));
};

// ─── Shogun shared-offer cache ────────────────────────────────────────────────

interface ShogunOfferCacheEntry {
  gloxTokenId: string;
  results: any[] | null;
  promise: Promise<any[]> | null;
}

let shogunOfferSharedCache: ShogunOfferCacheEntry | null = null;

/**
 * Returns true when the shared cache already holds resolved results for the
 * given token.  Callers can use this to skip triggering a loading state.
 */
export const isShogunOfferResolved = (gloxTokenId: string): boolean =>
  shogunOfferSharedCache?.gloxTokenId === gloxTokenId &&
  shogunOfferSharedCache.results !== null;

/**
 * Shared-promise manager for the Shogun offer API.
 *
 * Because the Shogun flow sends no product payload (only gloxTokenId) the
 * response is identical for every StaticProductContainer on the page.
 * This function ensures exactly one network request is made:
 *  - If results are already resolved → returns Promise.resolve(results)
 *  - If a fetch is in-flight        → returns the same promise
 *  - First caller                   → creates the fetch and stores the promise
 *
 * On failure the promise rejects — the caller is responsible for surfacing
 * the error (e.g. showing the telesales/error popup). The failure is NOT
 * cached, so a retry (e.g. tab switch) will trigger a fresh request.
 */
export const getShogunSharedOffer = (
  gloxTokenId: string,
  uniqueId: string,
  offerEndpoint: string
): Promise<any[]> => {
  // Already resolved
  if (isShogunOfferResolved(gloxTokenId)) {
    return Promise.resolve(shogunOfferSharedCache!.results!);
  }

  // In-flight
  if (
    shogunOfferSharedCache?.gloxTokenId === gloxTokenId &&
    shogunOfferSharedCache.promise !== null
  ) {
    return shogunOfferSharedCache.promise;
  }

  // First caller — start the real fetch
  const fetchPromise: Promise<any[]> = fetchShogunOfferData(gloxTokenId, uniqueId, offerEndpoint)
    .then((resp: any) => {
      const results: any[] = resp?.[uniqueId] ?? [];
      if (shogunOfferSharedCache?.gloxTokenId === gloxTokenId) {
        shogunOfferSharedCache.results = results;
        shogunOfferSharedCache.promise = null;
      }
      return results;
    })
    .catch((err: any) => {
      // Clear the cache entry so the next call retries the API.
      if (shogunOfferSharedCache?.gloxTokenId === gloxTokenId) {
        shogunOfferSharedCache = null;
      }
      throw err;
    });

  shogunOfferSharedCache = { gloxTokenId, results: null, promise: fetchPromise };
  return fetchPromise;
};


/**
 * Filters and sorts the Storyblok product list to match only the products
 * returned by the Shogun offer API, preserving the API response order.
 */
export const filterShogunProducts = (
  availableProducts: any[],
  shogunProductIds: string[]
): any[] => {
  const filtered = availableProducts.filter((product: any) => {
    const productId = product?.products?.[0]?.reference?.content?.product?.[0]?.productId;
    return shogunProductIds.includes(productId);
  });

  filtered.sort((a: any, b: any) => {
    const aId = a?.products?.[0]?.reference?.content?.product?.[0]?.productId;
    const bId = b?.products?.[0]?.reference?.content?.product?.[0]?.productId;
    return shogunProductIds.indexOf(aId) - shogunProductIds.indexOf(bId);
  });

  return filtered;
};
